import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Search, User, UserCircle2, Eye, MousePointerClick, ShoppingCart, Package, Clock } from 'lucide-react';
import { api } from '../../lib/api';
import { Pagination } from '../../components/admin/Pagination';
import { SegmentedTabs } from '../../components/admin/SegmentedTabs';
import { SortableTh, TableSkeleton } from '../../components/admin/SortableTable';
import { TableEmpty } from '../../components/admin/TableEmpty';
import { useDebounce } from '../../hooks/useDebounce';
import { useCurrency } from '../../context/CurrencyContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';

const TYPES = [
    { key: 'all', label: 'All' },
    { key: 'registered', label: 'Registered' },
    { key: 'anonymous', label: 'Anonymous' },
];

const CART_BADGE = {
    active: { bg: '#B7791F', label: 'Active cart' },
    converted: { bg: '#1F7A5A', label: 'Converted' },
    none: { bg: 'rgba(90,36,50,0.25)', label: '—' },
};

const EVENT_LABEL = {
    session_start: 'Started a session',
    page_view: 'Viewed a page',
    product_view: 'Viewed a product',
    add_to_cart: 'Added to bag',
    remove_from_cart: 'Removed from bag',
    request_submitted: 'Submitted a request',
};

export default function AdminAudience() {
    const [items, setItems] = useState([]);
    const [type, setType] = useState('all');
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [detail, setDetail] = useState(null);
    const [loadingDetail, setLoadingDetail] = useState(false);
    const PAGE_SIZE = 10;
    const debouncedSearch = useDebounce(search);
    const [sort, setSort] = useState({ by: 'last_seen', dir: 'desc' });
    const [loading, setLoading] = useState(true);
    const { format } = useCurrency();

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get('/admin/audience', { params: { page, page_size: PAGE_SIZE, search: debouncedSearch || undefined, type, sort_by: sort.by, sort_dir: sort.dir } });
            setItems(r.data.items);
            setTotal(r.data.total);
        } catch { toast.error('Failed to load audience.'); }
        finally { setLoading(false); }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, [page, debouncedSearch, type, sort]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { setPage(1); }, [debouncedSearch, type, sort]);

    const openDetail = async (visitorId) => {
        setLoadingDetail(true);
        setDetail({ loading: true });
        try {
            const r = await api.get(`/admin/audience/${visitorId}`);
            setDetail(r.data);
        } catch { toast.error('Failed to load profile.'); setDetail(null); }
        finally { setLoadingDetail(false); }
    };

    return (
        <div data-testid="admin-audience-page">
            <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
                <div>
                    <p className="eyebrow" style={{ color: 'var(--brand-burgundy)' }}>Atelier</p>
                    <h1 className="font-serif text-3xl sm:text-4xl mt-1" style={{ color: 'var(--brand-burgundy)' }}>Audience</h1>
                    <p className="text-xs mt-1" style={{ color: 'var(--ink-muted)' }}>A 360° view of every visitor and customer.</p>
                </div>
                <SegmentedTabs value={type} onChange={setType} options={TYPES} testIdPrefix="admin-audience-type" />
            </div>

            <div className="mb-4 flex items-center justify-between gap-4">
                <div className="relative max-w-sm w-full">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--ink-muted)' }} />
                    <input
                        data-testid="admin-audience-search-input"
                        type="text"
                        placeholder="Search by email, name or visitor id..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="input-luxury pl-9"
                    />
                </div>
                <p className="text-xs whitespace-nowrap hidden sm:block" style={{ color: 'var(--ink-muted)' }} data-testid="admin-audience-count">{total} visitor{total === 1 ? '' : 's'}</p>
            </div>

            <div className="luxury-card overflow-hidden">
                <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[820px]" data-testid="admin-audience-table">
                    <thead className="bg-[var(--brand-rose)]/40">
                        <tr className="text-left">
                            <th className="p-4">Visitor</th>
                            <th className="p-4">Type</th>
                            <SortableTh label="Visits" field="visit_count" sort={sort} onSort={setSort} />
                            <SortableTh label="Views" field="pageviews" sort={sort} onSort={setSort} />
                            <SortableTh label="Cart" field="cart_value" sort={sort} onSort={setSort} />
                            <SortableTh label="Requests" field="requests_count" sort={sort} onSort={setSort} />
                            <SortableTh label="Spent" field="total_spent" sort={sort} onSort={setSort} />
                            <SortableTh label="Last Seen" field="last_seen" sort={sort} onSort={setSort} />
                        </tr>
                    </thead>
                    {loading ? <TableSkeleton cols={8} /> : (
                    <tbody>
                        {items.map((v) => {
                            const badge = CART_BADGE[v.cart_status] || CART_BADGE.none;
                            return (
                                <tr key={v.visitor_id} onClick={() => openDetail(v.visitor_id)} className="border-t border-[rgba(90,36,50,0.08)] cursor-pointer hover:bg-[var(--brand-rose)]/20 transition-colors" data-testid={`admin-audience-row-${v.visitor_id}`}>
                                    <td className="p-4">
                                        <div className="flex items-center gap-2.5">
                                            {v.is_registered ? <UserCircle2 className="w-5 h-5" style={{ color: 'var(--brand-burgundy)' }} /> : <User className="w-5 h-5" style={{ color: 'var(--ink-muted)' }} />}
                                            <div className="min-w-0">
                                                <p className="font-serif text-base truncate" style={{ color: 'var(--brand-burgundy)' }}>{v.name || v.email || 'Guest visitor'}</p>
                                                <p className="text-[11px] font-mono truncate" style={{ color: 'var(--ink-muted)' }}>{v.email || v.visitor_id}</p>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="p-4"><span className="text-[10px] uppercase tracking-[0.14em] px-2 py-0.5 rounded-full" style={v.is_registered ? { backgroundColor: 'var(--brand-burgundy)', color: '#fff' } : { backgroundColor: 'rgba(90,36,50,0.10)', color: 'var(--ink-muted)' }}>{v.is_registered ? 'Registered' : 'Anonymous'}</span></td>
                                    <td className="p-4">{v.visit_count}</td>
                                    <td className="p-4">{v.pageviews} <span className="text-xs" style={{ color: 'var(--ink-muted)' }}>({v.product_views} prod)</span></td>
                                    <td className="p-4"><span className="text-[10px] uppercase tracking-[0.12em] px-2 py-0.5 rounded-full text-white" style={{ backgroundColor: badge.bg }}>{v.cart_status === 'active' ? `${v.cart_items} items` : badge.label}</span></td>
                                    <td className="p-4">{v.requests_count}</td>
                                    <td className="p-4">{format(v.total_spent || 0)}</td>
                                    <td className="p-4 text-xs" style={{ color: 'var(--ink-muted)' }}>{v.last_seen ? new Date(v.last_seen).toLocaleDateString() : '—'}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                    )}
                </table>
                </div>
                {!loading && items.length === 0 && (
                    <TableEmpty icon={User} title="No visitors yet" subtitle="Visitors and customers will appear here as they browse." testId="admin-audience-empty" />
                )}
            </div>

            <Pagination page={page} pageSize={PAGE_SIZE} total={total} onPageChange={setPage} testIdPrefix="admin-audience-pagination" />

            <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white" data-testid="admin-audience-detail-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-serif text-2xl" style={{ color: 'var(--brand-burgundy)' }}>Visitor 360°</DialogTitle>
                    </DialogHeader>
                    {loadingDetail || detail?.loading ? (
                        <p className="text-sm py-8 text-center" style={{ color: 'var(--ink-muted)' }}>Loading profile...</p>
                    ) : detail ? (
                        <div className="space-y-6">
                            <div className="flex items-center gap-3">
                                <div className="w-12 h-12 rounded-full flex items-center justify-center font-serif text-lg text-white" style={{ backgroundColor: 'var(--brand-burgundy)' }}>
                                    {((detail.customer?.name || detail.customer?.email || 'G')).charAt(0).toUpperCase()}
                                </div>
                                <div>
                                    <p className="font-serif text-xl" style={{ color: 'var(--brand-burgundy)' }}>{detail.customer?.name || detail.customer?.email || 'Anonymous visitor'}</p>
                                    <p className="text-xs font-mono" style={{ color: 'var(--ink-muted)' }}>{detail.customer?.email || detail.visitor?.visitor_id}</p>
                                </div>
                            </div>

                            <div className="grid grid-cols-3 gap-3">
                                {[
                                    ['Visits', detail.visitor?.visit_count || 0, Clock],
                                    ['Events', detail.events?.length || 0, MousePointerClick],
                                    ['Requests', detail.requests?.length || 0, Package],
                                ].map(([label, val, Icon]) => (
                                    <div key={label} className="rounded-xl p-3 text-center" style={{ backgroundColor: 'var(--brand-rose)' }}>
                                        <Icon className="w-4 h-4 mx-auto mb-1" style={{ color: 'var(--brand-burgundy)' }} />
                                        <p className="font-serif text-2xl" style={{ color: 'var(--brand-burgundy)' }}>{val}</p>
                                        <p className="eyebrow" style={{ color: 'var(--ink-muted)', fontSize: '0.6rem' }}>{label}</p>
                                    </div>
                                ))}
                            </div>

                            {detail.carts?.length > 0 && (
                                <div>
                                    <p className="eyebrow mb-2" style={{ color: 'var(--ink-muted)' }}>Carts</p>
                                    <ul className="space-y-2">
                                        {detail.carts.map((c) => (
                                            <li key={c.id} className="flex items-center justify-between text-sm border border-[rgba(90,36,50,0.10)] rounded-lg p-3">
                                                <span className="inline-flex items-center gap-2"><ShoppingCart className="w-4 h-4" style={{ color: 'var(--brand-burgundy)' }} /> {c.items?.length || 0} items · {format(c.subtotal || 0)}</span>
                                                <span className="text-[10px] uppercase tracking-[0.14em] px-2 py-0.5 rounded-full text-white" style={{ backgroundColor: c.status === 'converted' ? '#1F7A5A' : '#B7791F' }}>{c.status}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {detail.requests?.length > 0 && (
                                <div>
                                    <p className="eyebrow mb-2" style={{ color: 'var(--ink-muted)' }}>Purchase Requests</p>
                                    <ul className="space-y-2">
                                        {detail.requests.map((r) => (
                                            <li key={r.id} className="flex items-center justify-between text-sm border border-[rgba(90,36,50,0.10)] rounded-lg p-3">
                                                <span className="font-mono text-xs">{r.reference}</span>
                                                <span>{format(r.subtotal || 0)} · <span className="capitalize">{r.status}</span></span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            <div>
                                <p className="eyebrow mb-2" style={{ color: 'var(--ink-muted)' }}>Activity Timeline</p>
                                <ul className="space-y-2 max-h-64 overflow-y-auto">
                                    {(detail.events || []).map((e) => (
                                        <li key={e.id} className="flex items-start gap-3 text-sm">
                                            <span className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: 'var(--brand-burgundy)' }} />
                                            <div className="flex-1 min-w-0">
                                                <p style={{ color: 'var(--ink)' }}>{EVENT_LABEL[e.type] || e.type}{e.product_name ? `: ${e.product_name}` : ''}{e.path && !e.product_name ? ` — ${e.path}` : ''}</p>
                                                <p className="text-[11px]" style={{ color: 'var(--ink-muted)' }}>{new Date(e.created_at).toLocaleString()}</p>
                                            </div>
                                        </li>
                                    ))}
                                    {(!detail.events || detail.events.length === 0) && <li className="text-xs" style={{ color: 'var(--ink-muted)' }}>No activity recorded.</li>}
                                </ul>
                            </div>
                        </div>
                    ) : null}
                </DialogContent>
            </Dialog>
        </div>
    );
}
